import { useEffect, useRef } from "react";

const fract = (value) => value - Math.floor(value);
const seeded = (seed) => fract(Math.sin(seed * 12.9898 + 78.233) * 43758.5453);

function buildBolt(width, height, seed, size, xOffset) {
  const pointCount = Math.max(12, Math.round(18 + size * 7));
  const points = [];
  const startX = width * (0.66 + xOffset * 0.14);
  const endX = width * (0.48 + xOffset * 0.08);
  const startY = -height * 0.05;
  const endY = height * 1.04;

  for (let i = 0; i <= pointCount; i += 1) {
    const p = i / pointCount;
    const baseX = startX + (endX - startX) * p;
    const envelope = Math.sin(Math.PI * p);
    const coarseNoise = seeded(seed + i * 2.17) - 0.5;
    const fineNoise = seeded(seed * 1.31 + i * 7.73) - 0.5;
    const drift = Math.sin(p * 8.4 + seed) * width * 0.025;
    const jitter = (coarseNoise * width * 0.115 + fineNoise * width * 0.025) * envelope;
    points.push({
      x: baseX + drift + jitter,
      y: startY + (endY - startY) * p,
    });
  }

  const branches = [];
  const branchCount = 2 + Math.floor(seeded(seed + 91) * 2);
  for (let b = 0; b < branchCount; b += 1) {
    const anchorIndex = 4 + Math.floor(seeded(seed + 120 + b * 13) * (pointCount - 8));
    const anchor = points[anchorIndex];
    const dir = seeded(seed + 180 + b * 19) > 0.5 ? 1 : -1;
    const segments = 4 + Math.floor(seeded(seed + 220 + b * 23) * 4);
    const branch = [{ ...anchor }];
    let x = anchor.x;
    let y = anchor.y;
    for (let j = 1; j <= segments; j += 1) {
      const step = height * (0.028 + seeded(seed + b * 31 + j) * 0.02);
      x += dir * width * (0.018 + seeded(seed + 310 + b * 17 + j * 5) * 0.03);
      x += (seeded(seed + 410 + b * 7 + j * 11) - 0.5) * width * 0.026;
      y += step;
      branch.push({ x, y });
    }
    branches.push(branch);
  }

  return { points, branches };
}

function strokePath(ctx, points) {
  if (!points.length) return;
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const current = points[i];
    const midX = (prev.x + current.x) / 2;
    const midY = (prev.y + current.y) / 2;
    ctx.quadraticCurveTo(prev.x, prev.y, midX, midY);
  }
  const last = points[points.length - 1];
  ctx.lineTo(last.x, last.y);
  ctx.stroke();
}

/**
 * Original procedural electrical-arc layer for the SEM hero.
 * Canvas 2D only; intentionally independent from third-party component code.
 */
export function LightningHero({
  className = "",
  hue = 190,
  xOffset = -0.08,
  speed = 0.42,
  intensity = 0.2,
  size = 1.55,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return undefined;

    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches;

    let cssWidth = 1;
    let cssHeight = 1;
    let dpr = 1;
    let raf = 0;
    let visible = true;
    let lastFrame = 0;
    let boltSeed = 7.31;
    let previousSeed = 2.91;
    let nextChange = performance.now() + 900;
    const startedAt = performance.now();

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      cssWidth = Math.max(1, rect.width);
      cssHeight = Math.max(1, rect.height);
      dpr = Math.min(window.devicePixelRatio || 1, coarsePointer ? 1.15 : 1.5);
      const nextW = Math.max(1, Math.round(cssWidth * dpr));
      const nextH = Math.max(1, Math.round(cssHeight * dpr));
      if (canvas.width !== nextW || canvas.height !== nextH) {
        canvas.width = nextW;
        canvas.height = nextH;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const drawBolt = (bolt, alpha, branchFactor = 0.6) => {
      if (alpha <= 0.002) return;
      const color = `hsla(${hue}, 100%, 67%, ${alpha})`;

      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      ctx.shadowColor = `hsla(${hue}, 100%, 58%, ${Math.min(0.8, alpha * 2.4)})`;
      ctx.shadowBlur = 22;
      ctx.strokeStyle = color;
      ctx.lineWidth = 5.2;
      strokePath(ctx, bolt.points);

      ctx.shadowBlur = 11;
      ctx.strokeStyle = `hsla(${hue}, 100%, 72%, ${Math.min(0.9, alpha * 1.65)})`;
      ctx.lineWidth = 2.2;
      strokePath(ctx, bolt.points);

      ctx.shadowBlur = 3;
      ctx.strokeStyle = `rgba(232, 253, 255, ${Math.min(0.92, alpha * 1.42)})`;
      ctx.lineWidth = 0.8;
      strokePath(ctx, bolt.points);

      bolt.branches.forEach((branch) => {
        ctx.shadowBlur = 8;
        ctx.strokeStyle = `hsla(${hue}, 100%, 70%, ${alpha * branchFactor})`;
        ctx.lineWidth = 1.15;
        strokePath(ctx, branch);
      });

      ctx.restore();
    };

    const renderFrame = (now) => {
      ctx.clearRect(0, 0, cssWidth, cssHeight);

      if (!reducedMotion && now >= nextChange) {
        previousSeed = boltSeed;
        boltSeed += 3.73 + seeded(boltSeed + now * 0.0001) * 7.1;
        const baseDelay = 950 / Math.max(0.2, speed);
        nextChange = now + baseDelay * (0.58 + seeded(boltSeed + 33) * 0.78);
      }

      const time = reducedMotion ? 0.35 : (now - startedAt) / 1000;
      const pulse = reducedMotion
        ? 0.6
        : 0.32 + 0.68 * Math.pow(Math.max(0, Math.sin(time * (1.15 + speed * 0.7))), 8);
      const deviceFactor = coarsePointer ? 0.62 : 1;
      const alpha = intensity * deviceFactor * (0.45 + pulse * 0.95);

      const mainBolt = buildBolt(cssWidth, cssHeight, boltSeed, size, xOffset);
      drawBolt(mainBolt, alpha);

      if (!reducedMotion) {
        const ghost = buildBolt(cssWidth, cssHeight, previousSeed, size * 0.92, xOffset - 0.02);
        drawBolt(ghost, alpha * 0.16, 0.35);
      }
    };

    const animate = (now) => {
      raf = requestAnimationFrame(animate);
      if (!visible || document.hidden) return;
      const frameInterval = coarsePointer ? 46 : 36;
      if (now - lastFrame < frameInterval) return;
      lastFrame = now;
      renderFrame(now);
    };

    let observer = null;
    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        ([entry]) => {
          visible = entry.isIntersecting;
        },
        { threshold: 0.02 },
      );
    }

    const handleResize = () => {
      resize();
      if (reducedMotion) renderFrame(performance.now());
    };

    observer?.observe(canvas);
    window.addEventListener("resize", handleResize, { passive: true });
    resize();
    if (reducedMotion) {
      renderFrame(performance.now());
    } else {
      raf = requestAnimationFrame(animate);
    }

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", handleResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [hue, xOffset, speed, intensity, size]);

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute overflow-hidden ${className}`}
      style={{
        WebkitMaskImage:
          "linear-gradient(to right, transparent 0%, rgba(0,0,0,.2) 15%, #000 38%, #000 84%, transparent 100%)",
        maskImage:
          "linear-gradient(to right, transparent 0%, rgba(0,0,0,.2) 15%, #000 38%, #000 84%, transparent 100%)",
      }}
    >
      <canvas ref={canvasRef} className="h-full w-full opacity-90" />
    </div>
  );
}
